import React from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Radium from 'radium';
import config from 'config';
import {getMemberRole, tabButtonStyles, updateMemberRole} from 'utils/General';
import {
    changeUserPasswordForOwner,
    inviteMemberWithParams,
    reInviteMember,
    updateMemberChatLimit,
    updateMemberCurrentChatLimit
} from '../../../../../services/company/api';
import Translate from "../../../../../components/Translate";
import {handleValueChange} from "../../../../../utils/ComponentUtilFunctions";
import {updateOperator} from "../../../../../services/user/api";
import EmployeesTable from "./EmployeesTable";
import InviteEmployees from "./InviteEmployees";


Modal.setAppElement('#root');

const columnData = [
    {
        id: 'user', numeric: false, disablePadding: true, label: 'User',
    },
    {
        id: 'status', numeric: false, disablePadding: true, label: 'Status',
    },
    {
        id: 'currentStatus', numeric: false, disablePadding: true, label: 'currentStatus',
    },
    {
        id: 'role', numeric: false, disablePadding: true, label: 'Role',
    },
    {
        id: 'language', numeric: false, disablePadding: true, label: 'language',
    },
    {
        id: 'chatLimit', numeric: true, disablePadding: true, label: 'chatLimit',
    },
    {
        id: 'currentChatLimit', numeric: true, disablePadding: true, label: 'currentChatLimit',
    },
    {
        id: 'actions', numeric: false, disablePadding: true, label: '',
    },
];

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => b[orderBy] - a[orderBy] : (a, b) => a[orderBy] - b[orderBy];
}

const styles = {
    container: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        justifyContent: 'space-between',
        maxWidth: '95%',
        margin: '20px auto'
    },
    wrapper: {
        width: '100%',
        boxShadow: 'none',
        overflowX: 'auto',
    },
    avatar: {
        float: 'left',
        marginRight: '20px',
        width: '30px',
        height: '30px',
    },
    userName: {
        fontWeight: '100',
        fontSize: '14px',
    },
    userEmail: {
        fontSize: '12px',
    },
};

let roles = config.roles.filter((c) => c.id !== config.rolesMap.owner);
let locales = config.localesOptions;

class Employees extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            searchString: '',
            selected: [],
            page: 0,
            rowsPerPage: 5,
            chatLimit: {},
            currentChatLimit: {},
            role: roles[0],
            username: '',
            email: '',
            firstName: '',
            lastName: '',
            password: '',
            passwordsForChange: {},
            thumbUrl: '',
            canChangeHimselfInfo: false,
            language: locales[0],
            activeTab: 'Members',
        };
        this.handleChange = handleValueChange.bind(this);
    }

    getFilteredMembers = () => {
        const {searchString, selected} = this.state;
        const {members} = this.props;
        const lowerCaseSearchString = searchString.toLowerCase();
        return members.filter((member) => {
            const firstName = member.firstName ? member.firstName.toLowerCase() : '';
            const lastName = member.lastName ? member.lastName.toLowerCase() : '';
            const email = member.email ? member.email.toLowerCase() : '';

            const fullName = `${firstName} ${lastName}`.toLowerCase();
            const searchStringMatches =
                fullName.includes(lowerCaseSearchString) ||
                email.includes(searchString.toLowerCase());

            if (searchStringMatches) {
                return true;
            }

            return selected.indexOf(member.id) !== -1;
        });
    };

    getSelectedMembers = () => {
        const {members} = this.props;
        const {selected} = this.state;
        return members.filter(member => selected.indexOf(member.id) !== -1);
    };

    handleSelectAllClick = (checked) => {
        const {user} = this.props;
        if (checked) {
            const filteredMembers = this.getFilteredMembers().filter(fm => fm.email !== user.email);
            this.setState({selected: filteredMembers.map(n => n.id)});
            return;
        }
        this.setState({selected: []});
    };

    handleMemberSelectChange = id => (isSelected) => {
        const {selected} = this.state;

        if (isSelected) {
            this.setState({selected: [id, ...selected]});
        } else {
            this.setState({selected: selected.filter(curId => curId !== id)});
        }
    };

    handleChangePage = (page) => {
        this.setState({page});
    };

    handleChangeRowsPerPage = (value) => {
        this.setState({rowsPerPage: value, page: 0});
    };

    handleSearch = (searchString) => {
        this.handleChangePage(0)
        this.setState({
            searchString,
        });
    };

    isSelected = (id) => {
        const {selected} = this.state;
        return selected.indexOf(id) !== -1;
    };

    handleHimselfInfoCheckboxChange = () => {
        this.setState((prevState) => ({
            canChangeHimselfInfo: !prevState.canChangeHimselfInfo
        }));
    };

    handleMemberStatusChange = member => (statusId) => {
        if (statusId === member.status) {
            return;
        }
        const {updateMember} = this.props;

        const memberUpdateInfo = {id: member.id, status: statusId, isStatus: true};
        updateMember(memberUpdateInfo);
    };

    handleMemberCurrentStatusChange = member => (statusId) => {
        const {updateMember} = this.props;
        const memberUpdateInfo = {id: member.id, currentStatus: statusId, isCurrentStatus: true}
        updateMember(memberUpdateInfo)
    };

    handleMemberRoleChange = member => (roleId) => {
        if (getMemberRole(member).id !== roleId) {
            const {updateMember} = this.props;
            const {roles} = config;
            const newRole = roles.find(role => role.id === roleId);
            const memberUpdateInfo = updateMemberRole(member, newRole);
            updateMember(memberUpdateInfo);
        }
    };

    handleRemoveMembers = membersList => () => {
        const {removeMembers, confirmAction} = this.props;
        confirmAction(removeMembers, [membersList]);
    };

    reInviteMember = (memberInfo) => {
        const {addNotification} = this.props;
        reInviteMember(memberInfo.id).then(() => {
            addNotification('memberInvited');
        }).catch(() => {
            addNotification('somethingWentWrong', config.notificationLevels.error);
        });
    };

    handleChatLimitChange = (platformId) => (event) => {
        const chatLimitTarget = event.target.value;
        this.setState(prevState => ({
            chatLimit: {
                ...prevState.chatLimit,
                [platformId]: chatLimitTarget,
            }
        }));
    };

    handleMemberPasswordChange = (platformId) => (e) => {
        const password = e.target.value;
        this.setState((prevState) => ({
            passwordsForChange: {
                ...prevState.passwordsForChange,
                [platformId]: password
            }
        }));
    };

    handleMemberPasswordBlur = (platformId) => () => {
        const password = this.state.passwordsForChange[platformId];
        if (password !== undefined && password !== '') {
            changeUserPasswordForOwner(platformId, password)
                .then(() => {
                    this.props.addNotification('passwordChanged', config.notificationLevels.success);
                    this.setState((prevState) => ({
                        passwordsForChange: {
                            ...prevState.passwordsForChange,
                            [platformId]: "********"
                        }
                    }));
                })
                .catch(() => {
                    this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                });
        }    };

    handleCurrentChatLimitChange = (platformId) => (event) => {
        const currentChatLimitTarget = event.target.value;
        this.setState(prevState => ({
            currentChatLimit: {
                ...prevState.currentChatLimit,
                [platformId]: currentChatLimitTarget,
            }
        }));
    };

    handleChatLimitBlur = (platformId) => () => {
        const {chatLimit} = this.state;
        const chatLimitValue = chatLimit[platformId];

        if (chatLimitValue !== undefined && chatLimitValue !== '') {
            updateMemberChatLimit(platformId, chatLimitValue)
                .then(() => {
                    this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
                })
                .catch(() => {
                    this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                });
        }
    };

    handleCurrentChatLimitBlur = (platformId) => () => {
        const {currentChatLimit} = this.state;
        const currentChatLimitValue = currentChatLimit[platformId];

        if (currentChatLimitValue !== undefined && currentChatLimitValue !== '') {
            updateMemberCurrentChatLimit(platformId, currentChatLimitValue)
                .then(() => {
                    this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
                })
                .catch(() => {
                    this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
                });
        }
    };

    handleAddUser = () => {
        const {
            email, username, firstName, lastName, role, language, password, thumbUrl, canChangeHimselfInfo
        } = this.state;

        const {
            addNotification
        } = this.props;

        if (email && username && firstName && lastName && password) {
            inviteMemberWithParams({
                email, username, firstName,
                lastName, role: role.value ? role.value : role,
                language: language.value ? language.value : language, password, thumbUrl, canChangeHimselfInfo
            })
                .then(() => {
                    this.setState({
                        role: roles[0],
                        username: '',
                        email: '',
                        firstName: '',
                        lastName: '',
                        password: '',
                        thumbUrl: '',
                        canChangeHimselfInfo: false,
                        language: locales[0]
                    });
                    addNotification('memberAdded');
                    this.props.fetchCompany();
                }).catch((e) => {
                if (e.toString().includes("403")) {
                    addNotification('operatorLimitExceeded', config.notificationLevels.error);
                } else if (e.toString().includes("406")) {
                    addNotification('invalidEmail', config.notificationLevels.error);
                } else if (e.toString().includes("409")) {
                    addNotification('userEmailExists', config.notificationLevels.error);
                } else {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                }
            }).finally(() => {
                this.setState({
                    inviting: false,
                });
            });
        } else {
            addNotification('fillAllFields', config.notificationLevels.error);
        }
    }

    handleOperatorInfoChange = (userId, field, newValue) => {
        const {members} = this.props;
        const trimmedValue = newValue.trim();
        if (!trimmedValue) return;

        const originalUser = members.find(m => m.id === userId);
        if (!originalUser) return;

        if (originalUser[field] === trimmedValue) {
            return;
        }

        let data;
        if (field === 'firstName') {
            data = {
                firstName: newValue,
                lastName: originalUser.lastName,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        } else if (field === 'lastName') {
            data = {
                firstName: originalUser.firstName,
                lastName: newValue,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        } else if (field === 'thumbUrl') {
            data = {
                firstName: originalUser.firstName,
                lastName: originalUser.lastName,
                language: originalUser.language,
                contactNumber: '',
                thumbUrl: newValue
            }
        } else if (field === 'language') {
            data = {
                firstName: originalUser.firstName,
                lastName: originalUser.lastName,
                language: newValue,
                contactNumber: '',
                thumbUrl: originalUser.thumbUrl
            }
        }

        updateOperator(data, userId)
            .then(() => {

                if (this.props.fetchCompany) {
                    this.props.fetchCompany();
                }

                this.props.addNotification('userInfoUpdated', config.notificationLevels.success);
            })
            .catch(() => {
                this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
            });
    };

    render() {
        const {
            user,
            company,
            addNotification,
            commonStyles
        } = this.props;
        const {
            order, orderBy, rowsPerPage, page, searchString, chatLimit, currentChatLimit,
            email, username, firstName, lastName, role, language, password, thumbUrl, canChangeHimselfInfo, activeTab,
            passwordsForChange
        } = this.state;

        const filteredMembers = this.getFilteredMembers().filter(fm => fm.email !== user.email);
        const pageData = filteredMembers.sort(getSorting(order, orderBy))
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        let numSelected = 0;
        filteredMembers.forEach((member) => {
            numSelected += this.isSelected(member.id);
        });
        const selectedMembers = this.getSelectedMembers();

        return (
            <div>
                <div style={tabButtonStyles.tabButtons}>
                    <button style={tabButtonStyles.tabButton(activeTab === 'Members')}
                            onClick={() => this.setState({activeTab: 'Members'})}>
                        <Translate text="Members"/>
                    </button>
                    <button style={tabButtonStyles.tabButton(activeTab === 'inviteOrAddMembers')}
                            onClick={() => this.setState({activeTab: 'inviteOrAddMembers'})}>
                        <Translate text="inviteOrAddMembers"/>
                    </button>
                </div>

                <div style={styles.container}>
                    {activeTab === 'Members' && (
                        <div style={{padding: '32px', width: '100%'}} className="bg-white">
                            <EmployeesTable
                                passwordsForChange={passwordsForChange}
                                numSelected={numSelected}
                                selectedMembers={selectedMembers}
                                searchString={searchString}
                                handleSearch={this.handleSearch}
                                handleRemoveMembers={this.handleRemoveMembers}
                                user={user}
                                columnData={columnData}
                                order={order}
                                orderBy={orderBy}
                                filteredMembers={filteredMembers}
                                pageData={pageData}
                                chatLimit={chatLimit}
                                currentChatLimit={currentChatLimit}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                handleSelectAllClick={this.handleSelectAllClick}
                                isSelected={this.isSelected}
                                handleMemberSelectChange={this.handleMemberSelectChange}
                                handleOperatorInfoChange={this.handleOperatorInfoChange}
                                handleMemberStatusChange={this.handleMemberStatusChange}
                                handleMemberCurrentStatusChange={this.handleMemberCurrentStatusChange}
                                handleMemberRoleChange={this.handleMemberRoleChange}
                                handleChatLimitChange={this.handleChatLimitChange}
                                handleChatLimitBlur={this.handleChatLimitBlur}
                                handleCurrentChatLimitChange={this.handleCurrentChatLimitChange}
                                handleCurrentChatLimitBlur={this.handleCurrentChatLimitBlur}
                                reInviteMember={this.reInviteMember}
                                handleChangeRowsPerPage={this.handleChangeRowsPerPage}
                                handleChangePage={this.handleChangePage}
                                handleMemberPasswordChange={this.handleMemberPasswordChange}
                                handleMemberPasswordBlur={this.handleMemberPasswordBlur}
                            />
                        </div>
                    )}

                    {activeTab === 'inviteOrAddMembers' && (
                        <div style={{width: '100%'}}>
                            <InviteEmployees
                                user={user}
                                company={company}
                                addNotification={addNotification}
                                commonStyles={commonStyles}
                                fetchCompany={this.props.fetchCompany}
                                email={email}
                                username={username}
                                firstName={firstName}
                                lastName={lastName}
                                password={password}
                                thumbUrl={thumbUrl}
                                role={role}
                                language={language}
                                roles={roles}
                                locales={locales}
                                canChangeHimselfInfo={canChangeHimselfInfo}
                                handleChange={this.handleChange}
                                handleAddUser={this.handleAddUser}
                                handleHimselfInfoCheckboxChange={this.handleHimselfInfoCheckboxChange}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

Employees.propTypes = {
    user: PropTypes.object.isRequired,
    members: PropTypes.array.isRequired,
    sendSystemNotification: PropTypes.func.isRequired,
    updateMember: PropTypes.func.isRequired,
    removeMembers: PropTypes.func.isRequired,
    confirmAction: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
};

export default Radium(Employees);



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/members/Employees.jsx